import { useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router'
import {
  type ColumnDef,
  type OnChangeFn,
  type SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import {
  type PackagingShipmentListItem,
  type PackagingShipmentPageInfo,
} from '@/shared/hooks/api/packagingShipments'
import {
  type NavigateFn,
  useTableUrlState,
} from '@/shared/hooks/use-table-url-state'
import { Badge } from '@/components/ui/badge'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DataTableColumnHeader, DataTablePagination } from '@/components/data-table'

type ShipmentsTableProps = {
  items: PackagingShipmentListItem[]
  pageInfo: PackagingShipmentPageInfo
  isFetching?: boolean
  search: Record<string, unknown>
  navigate: NavigateFn
  sortBy: 'name' | 'skusUpdatedAt' | 'createdAt'
  sortDir: 'asc' | 'desc'
}

function formatCreatedAt(value: string, locale: string): string {
  return new Date(value).toLocaleString(locale)
}

export function ShipmentsTable({
  items,
  pageInfo,
  isFetching = false,
  search,
  navigate,
  sortBy,
  sortDir,
}: ShipmentsTableProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')
  const routerNavigate = useNavigate()

  const { pagination, onPaginationChange, ensurePageInRange } =
    useTableUrlState({
      search,
      navigate,
      pagination: { defaultPage: 1, defaultPageSize: 20 },
    })

  const sorting = useMemo<SortingState>(
    () => [{ id: sortBy, desc: sortDir === 'desc' }],
    [sortBy, sortDir]
  )

  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater
    const primary = next[0]
    const nextSortBy =
      primary?.id === 'name' ||
      primary?.id === 'skusUpdatedAt' ||
      primary?.id === 'createdAt'
        ? primary.id
        : 'createdAt'
    const nextSortDir = primary?.desc === true ? 'desc' : 'asc'
    navigate({
      search: (previous) => ({
        ...previous,
        sortBy: nextSortBy === 'createdAt' ? undefined : nextSortBy,
        sortDir: nextSortDir === 'desc' ? undefined : nextSortDir,
        page: undefined,
      }),
    })
  }

  const columns = useMemo<ColumnDef<PackagingShipmentListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('shipmentsPage.table.name')}
          />
        ),
        cell: ({ row }) => (
          <Link
            to={`/packaging/shipments/${row.original.id}`}
            className='text-primary underline'
            onClick={(event) => event.stopPropagation()}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'note',
        header: () => t('shipmentsPage.table.note'),
        enableSorting: false,
        cell: ({ row }) => {
          const note = row.original.note ?? ''
          return (
            <span
              className='inline-block w-64 max-w-64 truncate align-middle'
              title={note.length > 0 ? note : undefined}
            >
              {note}
            </span>
          )
        },
      },
      {
        accessorKey: 'skuCount',
        header: () => t('shipmentsPage.table.skuCount'),
        enableSorting: false,
        cell: ({ row }) => row.original.skuCount,
      },
      {
        id: 'attentionCount',
        header: () => t('shipmentsPage.table.attention'),
        enableSorting: false,
        cell: ({ row }) => {
          const count = row.original.attentionCount
          if (count == null) {
            return '—'
          }
          if (count > 0) {
            return <Badge variant='destructive'>{count}</Badge>
          }
          return count
        },
      },
      {
        id: 'declarationsReadyCount',
        header: () => t('shipmentsPage.table.declarationsReady'),
        enableSorting: false,
        cell: ({ row }) => row.original.declarationsReadyCount ?? '—',
      },
      {
        id: 'lastCheckedAt',
        header: () => t('shipmentsPage.table.lastChecked'),
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.lastCheckedAt == null) {
            return '—'
          }
          return formatCreatedAt(row.original.lastCheckedAt, i18n.language)
        },
      },
      {
        accessorKey: 'latestActiveUser',
        header: () => t('shipmentsPage.table.latestActiveUser'),
        enableSorting: false,
        cell: ({ row }) => row.original.latestActiveUser,
      },
      {
        accessorKey: 'skusUpdatedAt',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('shipmentsPage.table.skusLastChanged')}
          />
        ),
        cell: ({ row }) =>
          formatCreatedAt(row.original.skusUpdatedAt, i18n.language),
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('shipmentsPage.table.createdAt')}
          />
        ),
        cell: ({ row }) =>
          formatCreatedAt(row.original.createdAt, i18n.language),
      },
      {
        accessorKey: 'status',
        header: () => t('shipmentsPage.table.status'),
        enableSorting: false,
        cell: ({ row }) =>
          t(
            row.original.status === 'Updating'
              ? 'shipmentsPage.status.updating'
              : 'shipmentsPage.status.checked'
          ),
      },
    ],
    [i18n.language, t]
  )

  const table = useReactTable({
    data: items,
    columns,
    state: { pagination, sorting },
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    pageCount: pageInfo.totalPages,
    getCoreRowModel: getCoreRowModel(),
  })

  useEffect(() => {
    ensurePageInRange(pageInfo.totalPages)
  }, [ensurePageInRange, pageInfo.totalPages])

  return (
    <div
      className='flex min-h-0 flex-1 flex-col gap-4'
      aria-busy={isFetching}
    >
      <div className='min-h-0 flex-1 overflow-auto rounded-md border'>
        <table className='w-max min-w-full caption-bottom text-sm'>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className='cursor-pointer'
                onClick={() =>
                  routerNavigate(`/packaging/shipments/${row.original.id}`)
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </table>
      </div>
      <DataTablePagination table={table} />
    </div>
  )
}
